import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import StarIcon from "@mui/icons-material/Star";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import { useNavigate } from "react-router-dom";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { Hero, Product } from "../../api/homepage";
import {
  HeroRoot,
  HeroBackground,
  HeroContent,
  LeftSection,
  SellerPill,
  HeroTitle,
  HeroSubtitle,
  ButtonRow,
  PrimaryButton,
  SecondaryButton,
  StatsRow,
  StatItem,
  DropsCard,
  DropsHeader,
  DropsTitle,
  PrebookBadge,
  DropsListWrapper,
  VerticalCarousel,
  ProductDropCard,
  ProductImage,
  ProductInfo,
  ProductName,
  ProductMeta,
  ArrowButton,
} from "./HeroSectionCard.styles";

interface HeroSectionCardProps {
  hero?: Hero;
  prebookProducts?: Product[];
}

const HeroSectionCard: React.FC<HeroSectionCardProps> = ({
  prebookProducts = [],
}) => {
  const navigate = useNavigate();
  const orgId = localStorage.getItem("orgId");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const shouldUseCarousel = prebookProducts.length > 3;

  useEffect(() => {
    if (!shouldUseCarousel || prebookProducts.length === 0) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % prebookProducts.length);
        setIsAnimating(false);
      }, 500);
    }, 3000);

    return () => clearInterval(interval);
  }, [shouldUseCarousel, prebookProducts.length]);

  const handleProductClick = (productId: string) => {
    navigate(`/maltora-customer/products/product/${productId}`);
  };

  const handleShopCellar = () => {
    navigate(`/maltora-customer/products`);
  };

  const handleReserveUpcomingKegs = () => {
    const aboutSection = document.getElementById("about-us");
    aboutSection?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderProductCard = (product: Product, index: number) => {
    const productId = String(product.id ?? product._id);
    const imageUrl = product.imageMasterUrl || product.images?.[0]?.url || "";

    return (
      <ProductDropCard key={`${productId}-${index}`}>
        {imageUrl && <ProductImage src={imageUrl} alt={product.name} />}

        <ProductInfo>
          <ProductName>{product.name}</ProductName>
          <ProductMeta>
            ABV {product.abv ?? "-"}% · IBU {product.ibu ?? "-"}
          </ProductMeta>
        </ProductInfo>

        <ArrowButton onClick={() => handleProductClick(productId)}>
          <ArrowForwardIcon fontSize="small" />
        </ArrowButton>
      </ProductDropCard>
    );
  };

  const getVisibleProducts = () => {
    if (!shouldUseCarousel) {
      return prebookProducts.slice(0, 3);
    }

    const products = [];
    for (let i = 0; i < 4; i++) {
      const index = (currentIndex + i) % prebookProducts.length;
      products.push(prebookProducts[index]);
    }
    return products;
  };

  return (
    <HeroRoot data-testid="hero-section">
      <HeroBackground />

      <HeroContent>
        <LeftSection>
          <SellerPill>
            <LocalFireDepartmentIcon sx={{ fontSize: 14 }} />
            HOPWRIGHT BREWING CO. · SMALL-BATCH SINCE 2019
          </SellerPill>

          <HeroTitle>
            Craft beer your regulars will actually come back for.
          </HeroTitle>

          <HeroSubtitle>
            Small-batch kegs and bottles, cold-chained to your outlet in 48
            hours. One invoice. Net-30 for regulars.
          </HeroSubtitle>

          <ButtonRow>
            <PrimaryButton onClick={handleShopCellar}>
              Shop the cellar <ArrowForwardIcon sx={{ fontSize: 16 }} />
            </PrimaryButton>

            <SecondaryButton onClick={handleReserveUpcomingKegs}>
              Reserve upcoming kegs
            </SecondaryButton>
          </ButtonRow>

          <StatsRow>
            <StatItem>
              <StarIcon sx={{ fontSize: 14 }} />
              <strong>4.9</strong> outlet partner rating
            </StatItem>

            <StatItem>
              <span>·</span>
              <strong>1,200+</strong> outlets stocked
            </StatItem>

            <StatItem>
              <AcUnitIcon sx={{ fontSize: 14 }} />
              <strong>48 h</strong> cold-chain delivery
            </StatItem>
          </StatsRow>
        </LeftSection>

        {prebookProducts.length > 0 && (
          <DropsCard>
            <DropsHeader>
              <DropsTitle>UPCOMING DROPS</DropsTitle>
              <PrebookBadge>
                <EventAvailableIcon sx={{ fontSize: 12 }} />
                Prebook
              </PrebookBadge>
            </DropsHeader>

            <DropsListWrapper>
              {shouldUseCarousel ? (
                <VerticalCarousel $isAnimating={isAnimating}>
                  {getVisibleProducts().map((product, index) =>
                    renderProductCard(product, index)
                  )}
                </VerticalCarousel>
              ) : (
                <Box display="flex" flexDirection="column" gap="14px">
                  {prebookProducts.map((product, index) =>
                    renderProductCard(product, index)
                  )}
                </Box>
              )}
            </DropsListWrapper>
          </DropsCard>
        )}
      </HeroContent>
    </HeroRoot>
  );
};

export default HeroSectionCard;
